import React from "react";
import { BrowserRouter, Route } from "react-router-dom";
import {
  Admin,
  CanAccess,
  radiantLightTheme,
  radiantDarkTheme,
  Resource,
  CustomRoutes,
} from "react-admin";
import { dataProvider } from "./dataProvider";
import "./App.css";
import CveAnalytics from "./components/CveAnalytics";
import RmfAnalytics from "./components/RmfAnalytics";
import EndorScanPage from "./components/EndorScanPage";
import ReleaseComparison from "./components/ReleaseComparison";
import PreReleaseComparison from "./components/PreReleaseComparison";

// Import icons
import BugReportIcon from "@mui/icons-material/BugReport";
import VersionsIcon from "@mui/icons-material/ListAlt";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import SourceIcon from "@mui/icons-material/SourceRounded";
import CodeIcon from "@mui/icons-material/Code";
import WarningIcon from "@mui/icons-material/Warning";
import WidgetsIcon from "@mui/icons-material/Widgets";
import PeopleIcon from "@mui/icons-material/People";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import KeyIcon from "@mui/icons-material/Key";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import ScienceIcon from "@mui/icons-material/Science";

// Import resources
import {
  CodeSourceCreate,
  CodeSourceEdit,
  CodeSourceList,
  CodeSourceShow,
} from "./resources/codeSource";
import {
  CodeCveCreate,
  CodeCveEdit,
  CodeCveList,
  CodeCveShow,
} from "./resources/codeCve";
import {
  ImageSourceCreate,
  ImageSourceEdit,
  ImageSourceList,
  ImageSourceShow,
} from "./resources/imageSource";
import {
  ImageBuildCreate,
  ImageBuildEdit,
  ImageBuildList,
  ImageBuildShow,
} from "./resources/imageBuild";
import {
  ImageCveCreate,
  ImageCveEdit,
  ImageCveList,
  ImageCveShow,
} from "./resources/imageCve";
import {
  ReleaseCreate,
  ReleaseEdit,
  ReleaseList,
  ReleaseShow,
} from "./resources/release";
import {
  TrackedCveCreate,
  TrackedCveEdit,
  TrackedCveList,
  TrackedCveShow,
} from "./resources/trackedCVE";
import { SbomList, SbomShow, SbomEdit, SbomCreate } from "./resources/sboms";
import {
  PreReleaseList,
  PreReleaseShow,
  PreReleaseEdit,
} from "./resources/preRelease";
import {
  EmployeeCreate,
  EmployeeEdit,
  EmployeeList,
  EmployeeShow,
} from "./resources/employee";
import {
  ApiTokenList,
  ApiTokenShow,
  ApiTokenEdit,
  ApiTokenCreate,
} from "./resources/apiToken";

import { MyLayout } from "./components/MyLayout";
import OktaLoginPage from "./components/OktaLoginPage";
import { authProvider } from "./authProvider";
import Dashboard from "./components/Dashboard";
import { RiskList, RiskShow, RiskEdit, RiskCreate } from "./resources/risk";

const App = () => (
  <BrowserRouter>
    <Admin
      theme={radiantLightTheme}
      darkTheme={radiantDarkTheme}
      dashboard={Dashboard}
      dataProvider={dataProvider}
      authProvider={authProvider}
      layout={MyLayout}
      loginPage={<OktaLoginPage onLogin={() => authProvider.login({})} />}
      requireAuth
    >
      <CustomRoutes>
        <Route path="cve/analytics" element={<CveAnalytics />} />
        <Route
          path="cve/endor-scan"
          element={
            <CanAccess action="show" resource="cve/endor-scan">
              <EndorScanPage />
            </CanAccess>
          }
        />
        <Route
          path="cve/compare-releases"
          element={
            <CanAccess action="show" resource="cve/comparison">
              <ReleaseComparison />
            </CanAccess>
          }
        />
        <Route
          path="cve/pre-releases/:id/compare"
          element={
            <CanAccess action="show" resource="cve/pre-releases">
              <PreReleaseComparison />
            </CanAccess>
          }
        />
      </CustomRoutes>
      <Resource
        name="cve/code-sources"
        list={CodeSourceList}
        edit={CodeSourceEdit}
        create={CodeSourceCreate}
        show={CodeSourceShow}
        icon={CodeIcon}
        options={{
          label: "Code Sources",
        }}
      />

      <Resource
        name="cve/code-cves"
        list={CodeCveList}
        edit={CodeCveEdit}
        create={CodeCveCreate}
        show={CodeCveShow}
        icon={BugReportIcon}
        options={{
          label: "Code CVEs",
        }}
      />
      <Resource
        name="cve/image-sources"
        list={ImageSourceList}
        edit={ImageSourceEdit}
        create={ImageSourceCreate}
        show={ImageSourceShow}
        icon={SourceIcon}
        options={{
          label: "Image Sources",
        }}
      />
      <Resource
        name="cve/image-builds"
        list={ImageBuildList}
        edit={ImageBuildEdit}
        create={ImageBuildCreate}
        show={ImageBuildShow}
        icon={WidgetsIcon}
        options={{
          label: "Image Builds",
        }}
      />
      <Resource
        name="cve/image-cves"
        list={ImageCveList}
        edit={ImageCveEdit}
        create={ImageCveCreate}
        show={ImageCveShow}
        icon={WarningIcon}
        options={{
          label: "Image CVEs",
        }}
      />
      <Resource
        name="cve/releases"
        list={ReleaseList}
        edit={ReleaseEdit}
        create={ReleaseCreate}
        show={ReleaseShow}
        icon={VersionsIcon}
        options={{
          label: "Releases",
        }}
      />
      <Resource
        name="cve/tracked-cves"
        list={TrackedCveList}
        edit={TrackedCveEdit}
        create={TrackedCveCreate}
        show={TrackedCveShow}
        icon={TrackChangesIcon}
        options={{
          label: "Tracked CVEs",
        }}
      />
      <Resource
        name="cve/sboms"
        list={SbomList}
        edit={SbomEdit}
        create={SbomCreate}
        show={SbomShow}
        options={{
          label: "Uploaded SBOMs",
        }}
      />
      <Resource
        name="cve/pre-releases"
        list={PreReleaseList}
        edit={PreReleaseEdit}
        show={PreReleaseShow}
        icon={ScienceIcon}
        options={{
          label: "Pre-Releases",
        }}
      />
      <CustomRoutes>
        <Route path="rmf/analytics" element={<RmfAnalytics />} />
      </CustomRoutes>
      <Resource
        name="rmf/employees"
        list={EmployeeList}
        edit={EmployeeEdit}
        create={EmployeeCreate}
        show={EmployeeShow}
        icon={PeopleIcon}
        options={{
          label: "Employees",
        }}
      />
      <Resource
        name="rmf/risks"
        list={RiskList}
        edit={RiskEdit}
        create={RiskCreate}
        show={RiskShow}
        icon={GppMaybeIcon}
        options={{
          label: "Risk Tickets",
        }}
      />
      <Resource
        name="admin/api-tokens"
        list={ApiTokenList}
        edit={ApiTokenEdit}
        create={ApiTokenCreate}
        show={ApiTokenShow}
        icon={KeyIcon}
        options={{
          label: "API Tokens",
        }}
      />
    </Admin>
  </BrowserRouter>
);

export default App;
